import type { RUMEntry } from "@fiverr-private/relay-sdk";
import { describe } from "../describe";
import { performanceEntries } from "../performanceEntries";
import { processEntry } from "../processEntry";
import { sorter } from "../sorter";

const MAX_INTERACTIONS_TO_CONSIDER = 10;
const longestInteractionList: Interaction[] = [];
const longestInteractionMap = new Map<string, Interaction>();

interface Interaction {
    id: any;
    latency: number;
    startTime: number;
    target: Node;
    names: Set<string>;
    entries: PerformanceEventTiming[];
}

/**
 * Entry has real interaction ID
 */
const hasId = (
    { interactionId }: PerformanceEventTiming = {} as PerformanceEventTiming,
): boolean => !Number.isNaN(interactionId) && interactionId > -1;

/**
 * P98 on interactionCount
 */
const countInteractions = (entries: PerformanceEventTiming[]): number =>
    Math.floor(
        Object.hasOwnProperty.call(performance, "interactionCount")
            ? (performance as any).interactionCount
            : (entries.filter(hasId).reduce(
                  ({ min, max }, { interactionId }) => {
                      min = Math.min(min, interactionId);
                      max = Math.max(max, interactionId);
                      return {
                          min,
                          max,
                          interactionCount: max ? (max - min) / 7 + 1 : 0,
                      };
                  },
                  {
                      min: Infinity,
                      max: 0,
                      interactionCount: 0,
                  },
              )?.interactionCount ?? 0 / 50),
    );

function entryHandler(entry: PerformanceEventTiming): void {
    if (!hasId(entry)) {
        return;
    }
    const minLongestInteraction = longestInteractionList.at(-1) || {
        latency: 0,
    };
    const existingInteraction = longestInteractionMap.get(entry.interactionId);

    // Conditions for a new potentially longest interaction
    const listHasVacancies =
        longestInteractionList.length < MAX_INTERACTIONS_TO_CONSIDER;
    const largerThanMin = entry.duration > minLongestInteraction.latency;

    if (existingInteraction || listHasVacancies || largerThanMin) {
        if (existingInteraction) {
            existingInteraction.entries.push(entry);
            existingInteraction.latency = Math.max(
                existingInteraction.latency,
                entry.duration,
            );
            existingInteraction.names.add(entry.name);
            existingInteraction.target =
                existingInteraction.target || entry.target;
        } else {
            const interaction = {
                id: entry.interactionId,
                latency: entry.duration,
                startTime: entry.startTime,
                target: entry.target,
                names: new Set([entry.name]),
                entries: [entry],
            };
            longestInteractionMap.set(interaction.id, interaction);
            longestInteractionList.push(interaction);
        }

        // Sort the entries by latency, desc
        longestInteractionList.sort(sorter("latency"));

        // keep only the top ten
        longestInteractionList
            .splice(MAX_INTERACTIONS_TO_CONSIDER)
            .forEach((entry: Interaction): boolean =>
                longestInteractionMap.delete(entry.id),
            );
    }
}

export function interactionToNextPaintMark(): RUMEntry[] {
    if (!PerformanceObserver.supportedEntryTypes.includes("event")) {
        return [];
    }

    const entries = performanceEntries({ type: "event" });
    const firstInteraction = performanceEntries({
        type: "first-input",
        buffered: true,
    }).find(({ interactionId }) => interactionId > 0);

    firstInteraction && entries.push(firstInteraction);
    const interactionsCount = countInteractions(
        entries as PerformanceEventTiming[],
    );
    entries.forEach(entryHandler);
    const candidateInteractionIndex = Math.min(
        longestInteractionList.length - 1,
        interactionsCount,
    );
    if (candidateInteractionIndex === -1) {
        return [];
    }

    const {
        latency: duration,
        names,
        startTime,
        target,
    } = longestInteractionList[candidateInteractionIndex];

    const entry = processEntry({
        counter: entries.filter(hasId).length,
        duration,
        entryType: "mark",
        name: "interaction_to_next_paint",
        user_action: Array.from(names).join(" "),
        startTime,
        target,
        toJSON: function () {
            return {
                counter: this.counter,
                duration: this.duration,
                entryType: this.entryType,
                name: this.name,
                user_action: this.user_action,
                startTime: this.startTime,
                target: describe(this.target),
            };
        },
    });

    return [entry];
}
